import { supabase } from '@/lib/supabase'
import type { Nation, NationCreate, NationUpdate, JoinableNation, Party } from '@/types'

// Query key factory for consistent cache management
export const nationKeys = {
  all: ['nations'] as const,
  byWorld: (worldId: string) => ['nations', 'world', worldId] as const,
  detail: (id: string) => ['nations', id] as const,
  joinable: () => ['nations', 'joinable'] as const,
}

export async function fetchNationsByWorld(worldId: string): Promise<Nation[]> {
  const { data, error } = await supabase
    .from('nations')
    .select('*')
    .eq('world_id', worldId)
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  return data as Nation[]
}

export async function fetchNation(id: string): Promise<Nation> {
  const { data, error } = await supabase.from('nations').select('*').eq('id', id).single()

  if (error) {
    throw error
  }

  return data as Nation
}

export async function createNation(nation: NationCreate): Promise<Nation> {
  const { data, error } = await supabase.from('nations').insert(nation).select().single()

  if (error) {
    throw error
  }

  return data as Nation
}

export async function updateNation(id: string, updates: NationUpdate): Promise<Nation> {
  const { data, error } = await supabase
    .from('nations')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Nation
}

export async function deleteNation(id: string): Promise<void> {
  const { error } = await supabase.from('nations').delete().eq('id', id)

  if (error) {
    throw error
  }
}

export async function fetchJoinableNations(): Promise<JoinableNation[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  // Fetch nations with their worlds and parties
  // Include 'active' and 'setup' worlds (exclude 'paused' and 'completed')
  const { data, error } = await supabase
    .from('nations')
    .select(
      `
      *,
      world:worlds!inner(id, name, status),
      parties(id, name, user_id, abbreviation, color, slogan, ideology_vector, money, action_points, prestige, momentum, traits, created_tick, is_active, nation_id)
    `
    )
    .in('world.status', ['active', 'setup'])
    .order('name', { ascending: true })

  if (error) {
    throw error
  }

  // Process the data to calculate party counts and find user's party
  return (data || []).map((item) => {
    const parties = (item.parties || []) as Party[]
    const activeParties = parties.filter((p) => p.is_active)
    const userParty = user ? parties.find((p) => p.user_id === user.id) || null : null

    return {
      nation: {
        id: item.id,
        world_id: item.world_id,
        name: item.name,
        government_type: item.government_type,
        electoral_system: item.electoral_system,
        term_length_ticks: item.term_length_ticks,
        threshold_percent: item.threshold_percent,
        max_parties: item.max_parties,
        confidence_votes_enabled: item.confidence_votes_enabled,
        judicial_review_strength: item.judicial_review_strength,
        media_freedom: item.media_freedom,
        campaign_restrictions: item.campaign_restrictions,
        current_government_id: item.current_government_id,
        next_election_tick: item.next_election_tick,
      } as Nation,
      world: item.world as { id: string; name: string; status: 'active' },
      partyCount: activeParties.length,
      userParty,
    }
  })
}
